import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IonButton,
  IonIcon,
  IonCard,
  IonItem,
  IonToast,
  IonAlert,
} from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import { linkOutline, checkmarkCircle, closeCircle, logoGithub } from 'ionicons/icons';
import McpInstallCard from './McpInstallCard';
import {
  useAllIntegrationStatuses,
  useInvalidateIntegrations,
  useDisconnectIntegration,
  useStartOAuth,
  useGitlabIntegrations
} from '../../hooks/useIntegrations';
import GitlabConnectModal from '../../components/integrations/GitlabConnectModal';
import LarkConnectModal from '../../components/integrations/LarkConnectModal';
import OpenAIBillingConnectModal from '../../components/integrations/OpenAIBillingConnectModal';
import AnthropicBillingConnectModal from '../../components/integrations/AnthropicBillingConnectModal';
import PlaneConnectModal from '../../components/integrations/PlaneConnectModal';
import LinearConnectModal from '../../components/integrations/LinearConnectModal';
import CircleCIConnectModal from '../../components/integrations/CircleCIConnectModal';
import FigmaConnectModal from '../../components/integrations/FigmaConnectModal';
import OpenDesignConnectModal from '../../components/integrations/OpenDesignConnectModal';
import api from '../../services/api';
import './IntegrationsPage.css';

const IntegrationsPage = ({ pageRef }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const statusQuery = useAllIntegrationStatuses();
  const invalidateIntegrations = useInvalidateIntegrations();
  // GitLab renders as TWO cards (Self-Hosted + Cloud). /integrations/status
  // collapses gitlab to one provider row, so we pull the full list and match
  // each card to its instance by hostname. Served by the Webhooks RestApi
  // (GET /gitlab/integrations) — no tokens.
  const gitlabIntegrationsQuery = useGitlabIntegrations();
  
  // Mutations
  const disconnectJira = useDisconnectIntegration('jira');
  const disconnectGithub = useDisconnectIntegration('github');
  const disconnectGitlab = useDisconnectIntegration('gitlab');
  const disconnectSentry = useDisconnectIntegration('sentry');
  const disconnectSlack = useDisconnectIntegration('slack');
  const disconnectLark = useDisconnectIntegration('lark');
  const disconnectOpenAIBilling = useDisconnectIntegration('openai_billing');
  const disconnectAnthropicBilling = useDisconnectIntegration('anthropic_billing');
  const disconnectNotion = useDisconnectIntegration('notion');
  const disconnectPlane = useDisconnectIntegration('plane');
  const disconnectLinear = useDisconnectIntegration('linear');
  const disconnectCircleci = useDisconnectIntegration('circleci');
  const disconnectFigma = useDisconnectIntegration('figma');
  const startJiraOAuth = useStartOAuth('jira');
  const startGithubOAuth = useStartOAuth('github');
  const startSentryOAuth = useStartOAuth('sentry');
  const startSlackOAuth = useStartOAuth('slack');
  const startNotionOAuth = useStartOAuth('notion');

  // Local UI state — modal open flags. Form state lives inside the
  // shared {Gitlab,Lark,OpenAIBilling,AnthropicBilling}
  // ConnectModal components.
  const [gitlabModalOpen, setGitlabModalOpen] = useState(false);
  // Which GitLab the Connect modal is wired for: 'cloud' locks the URL to
  // gitlab.com (token-only), 'selfhosted' lets the user type their server URL.
  const [gitlabMode, setGitlabMode] = useState('selfhosted');
  const [larkModalOpen, setLarkModalOpen] = useState(false);
  const [openaiBillingModalOpen, setOpenaiBillingModalOpen] = useState(false);
  const [anthropicBillingModalOpen, setAnthropicBillingModalOpen] = useState(false);
  const [planeModalOpen, setPlaneModalOpen] = useState(false);
  const [linearModalOpen, setLinearModalOpen] = useState(false);
  const [circleciModalOpen, setCircleciModalOpen] = useState(false);
  const [figmaModalOpen, setFigmaModalOpen] = useState(false);
  const [openDesignModalOpen, setOpenDesignModalOpen] = useState(false);
  // OpenDesign has its OWN REST route (/integrations/open-design) — it's NOT
  // part of the aggregated /integrations/status payload — so its connection
  // state is fetched + held locally here rather than read off statusQuery.data.
  const [openDesignData, setOpenDesignData] = useState({});
  // NOTE: Claude / OpenAI (Codex) DEPLOY credentials are NOT managed here.
  // They're per-project AI keys (project `secrets.aiOAuthToken` /
  // `aiApiKey`), set in Project Settings → AI and used by that project's
  // apps + workflows. The account-level workspace-credentials store + its
  // paste-token cards were removed (2026-06-15 credential-scoping fix). The
  // OpenAI Admin / Anthropic Admin cards below are SPEND-TRACKING org keys
  // (integrations service), unrelated to deploy creds.

  const [toast, setToast] = useState({ isOpen: false, message: '', color: 'primary', icon: null });
  const [disconnectAlert, setDisconnectAlert] = useState({ isOpen: false, type: 'jira' });

  const showToast = (message, type = 'primary') => {
    let icon;
    switch(type) {
      case 'success':
        icon = checkmarkCircle;
        break;
      case 'danger':
        icon = closeCircle;
        break;
      default:
        icon = null;
    }
    setToast({ isOpen: true, message, color: type, icon });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    // OAuth callbacks redirect to /integrations?<service>=success|error.
    // Notion's callback (served by the llm-billing RestApi) follows the
    // same convention, so add it here.
    const services = ['jira', 'github', 'sentry', 'slack', 'notion', 'figma'];
    const labels = { jira: 'Jira', github: 'GitHub', sentry: 'Sentry', slack: 'Slack', notion: 'Notion', figma: 'Figma' };
    let handled = false;

    for (const service of services) {
      const status = searchParams.get(service);
      if (status === 'success') {
        showToast(`${labels[service]} connected successfully!`, 'success');
        invalidateIntegrations();
        handled = true;
        break;
      } else if (status === 'error') {
        showToast(`Failed to connect ${labels[service]}. Please try again.`, 'danger');
        handled = true;
        break;
      }
    }

    if (handled) {
      navigate('/integrations', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleJiraConnect = () => {
    startJiraOAuth.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.authUrl;
      },
      onError: (error) => {
        showToast('Failed to connect Jira. Please try again.', 'danger');
      }
    });
  };

  const handleGithubConnect = () => {
    const githubData = statusQuery.data?.github || {};
    if (githubData.connected) {
      const shouldContinue = window.confirm(
        'GitHub is already connected. To see the consent screen and grant repo scope for private repos:\n\n' +
        '1. Click OK to open GitHub Settings\n' +
        '2. Find "Zibby Code" and click "Revoke"\n' +
        '3. Come back and click Connect again\n\n' +
        'This is required because GitHub doesn\'t allow forcing consent screen programmatically.'
      );
      
      if (shouldContinue) {
        window.open('https://github.com/settings/applications', '_blank');
      }
      return;
    }

    startGithubOAuth.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.authUrl;
      },
      onError: (error) => {
        showToast('Failed to connect GitHub. Please try again.', 'danger');
      }
    });
  };

  const handleGitlabConnect = (mode = 'selfhosted') => { setGitlabMode(mode); setGitlabModalOpen(true); };
  const handleLarkConnect = () => setLarkModalOpen(true);

  const handleSentryConnect = () => {
    startSentryOAuth.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.authUrl;
      },
      onError: (error) => {
        showToast('Failed to connect Sentry. Please try again.', 'danger');
      }
    });
  };

  const handleSlackConnect = () => {
    startSlackOAuth.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.authUrl;
      },
      onError: (error) => {
        showToast('Failed to connect Slack. Please try again.', 'danger');
      }
    });
  };

  const handleOpenAIBillingConnect = () => setOpenaiBillingModalOpen(true);
  const handleAnthropicBillingConnect = () => setAnthropicBillingModalOpen(true);
  const handlePlaneConnect = () => setPlaneModalOpen(true);
  const handleLinearConnect = () => setLinearModalOpen(true);
  const handleCircleciConnect = () => setCircleciModalOpen(true);

  const handleNotionConnect = () => {
    startNotionOAuth.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.authUrl;
      },
      onError: (error) => {
        // 404 here = Agent A hasn't deployed the /notion routes yet.
        // Surface the actual error message so the gap is visible in
        // local dev rather than silently swallowed.
        showToast(error?.message
          ? `Failed to connect Notion: ${error.message}`
          : 'Failed to connect Notion. Please try again.', 'danger');
      }
    });
  };

  // Figma is now a paste-token (PAT) connector — open the modal, same as
  // Plane/Linear. (Previously this kicked off an OAuth redirect.)
  const handleFigmaConnect = () => setFigmaModalOpen(true);

  // OpenDesign — manual URL+token connector with its own REST route
  // (/integrations/open-design), so its status is fetched independently of the
  // aggregated /integrations/status payload.
  const refreshOpenDesign = () => {
    api.get('/integrations/open-design')
      .then((data) => setOpenDesignData(data || {}))
      .catch(() => setOpenDesignData({})); // absent/404 → render "Connect"
  };

  useEffect(() => {
    refreshOpenDesign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDesignConnect = () => setOpenDesignModalOpen(true);

  const handleOpenDesignDisconnect = () => {
    api.delete('/integrations/open-design')
      .then(() => {
        setOpenDesignData({});
        showToast('OpenDesign disconnected successfully.', 'success');
      })
      .catch(() => showToast('Failed to disconnect OpenDesign.', 'danger'));
  };

  // Deep-link auto-open: the marketplace "Connect" button navigates here as
  // /integrations?provider=<id>. For providers that connect via OUR paste-token
  // MODAL, pop that modal straight away so the user lands on the connect form.
  // Redirect/OAuth providers (jira, github, sentry, slack, notion) are
  // intentionally NOT listed — auto-firing those would bounce the user off to an
  // OAuth page on page load.
  useEffect(() => {
    const provider = new URLSearchParams(location.search).get('provider');
    if (!provider) return;
    const modalOpeners = {
      gitlab: () => handleGitlabConnect('selfhosted'),
      lark: handleLarkConnect,
      openai_billing: handleOpenAIBillingConnect,
      anthropic_billing: handleAnthropicBillingConnect,
      plane: handlePlaneConnect,
      linear: handleLinearConnect,
      circleci: handleCircleciConnect,
      figma: handleFigmaConnect,
      'open-design': handleOpenDesignConnect,
    };
    const open = modalOpeners[provider];
    if (!open) return;                                   // redirect-type / unknown → leave alone
    // OpenDesign isn't in the aggregated statusQuery payload — it has its own
    // route + local state. Gate it on openDesignData instead and don't block on
    // statusQuery loading.
    if (provider === 'open-design') {
      if (!openDesignData.connected) open();
      navigate('/integrations', { replace: true });
      return;
    }
    if (statusQuery.isLoading) return;                   // wait for connection status first
    if (!statusQuery.data?.[provider]?.connected) open();// don't pop for an already-connected provider
    navigate('/integrations', { replace: true });        // clear the param either way
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, statusQuery.isLoading, statusQuery.data]);

  const showDisconnectConfirmation = (type = 'jira', integrationId = null) => {
    setDisconnectAlert({ isOpen: true, type, integrationId });
  };

  const handleDisconnectConfirmed = async () => {
    const { type, integrationId } = disconnectAlert;
    const alertType = type || 'jira';
    setDisconnectAlert({ isOpen: false, type: 'jira' });

    const mutations = {
      jira: disconnectJira,
      github: disconnectGithub,
      gitlab: disconnectGitlab,
      sentry: disconnectSentry,
      slack: disconnectSlack,
      lark: disconnectLark,
      openai_billing: disconnectOpenAIBilling,
      anthropic_billing: disconnectAnthropicBilling,
      notion: disconnectNotion,
      plane: disconnectPlane,
      linear: disconnectLinear,
      circleci: disconnectCircleci,
      figma: disconnectFigma,
    };

    const labels = {
      jira: 'Jira',
      github: 'GitHub',
      gitlab: 'GitLab',
      sentry: 'Sentry',
      slack: 'Slack',
      lark: 'Lark',
      openai_billing: 'OpenAI Admin',
      anthropic_billing: 'Anthropic Admin',
      notion: 'Notion',
      plane: 'Plane',
      linear: 'Linear',
      circleci: 'CircleCI',
      figma: 'Figma',
    };

    // GitLab is multi-instance — disconnect the SPECIFIC instance the card is
    // for (pass its integrationId). Every other provider is account-wide (undefined).
    const mutateArg = (alertType === 'gitlab' && integrationId) ? { integrationId } : undefined;
    mutations[alertType]?.mutate(mutateArg, {
      onSuccess: () => {
        showToast(`${labels[alertType]} disconnected successfully.`, 'success');
      },
      onError: (error) => {
        showToast(`Failed to disconnect ${labels[alertType]}.`, 'danger');
      }
    });
  };

  const handleDisconnectCancelled = () => {
    setDisconnectAlert({ isOpen: false, type: 'jira' });
  };
  
  const jiraData = statusQuery.data?.jira || {};
  const githubData = statusQuery.data?.github || {};
  // The account's gitlab integrations (cloud + self-hosted) from the dedicated
  // list endpoint — the single collapsed /integrations/status gitlab row can't
  // distinguish the two cards. Each card below matches its instance by hostname.
  const gitlabInstances = gitlabIntegrationsQuery.data?.integrations || [];
  // GitLab renders as TWO permanent cards (Self-Hosted + Cloud); each card's
  // connected state is matched out of this list by instanceUrl. A reused icon
  // so both cards share the GitLab mark.
  const gitlabIcon = (
    <div className="gitlab-icon-wrapper" slot="start">
      <svg className="integration-icon gitlab" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
        <path d="M23.955 13.587l-1.342-4.135-2.664-8.189a.455.455 0 00-.867 0L16.418 9.45H7.582L4.918 1.263a.455.455 0 00-.867 0L1.386 9.452.045 13.587a.924.924 0 00.331 1.023L12 23.054l11.624-8.443a.92.92 0 00.331-1.024" fill="#E24329"/>
        <path d="M12 23.054L16.418 9.45H7.582L12 23.054z" fill="#FC6D26"/>
        <path d="M12 23.054l-4.418-13.6H1.386L12 23.054z" fill="#FCA326"/>
        <path d="M1.386 9.452L.045 13.587a.924.924 0 00.331 1.023L12 23.054 1.386 9.452z" fill="#E24329"/>
        <path d="M12 23.054l4.418-13.6h6.196L12 23.054z" fill="#FCA326"/>
        <path d="M22.614 9.452l1.341 4.135a.924.924 0 01-.331 1.023L12 23.054l10.614-13.602z" fill="#E24329"/>
      </svg>
    </div>
  );
  // Classify cloud vs self-hosted by EXACT hostname — a substring/prefix test
  // would misread the common self-hosted vanity host `gitlab.company.com` as
  // gitlab.com and bind the wrong card. Parse the host and compare exactly.
  const isGitlabDotCom = (url) => {
    try {
      const h = new URL(/^https?:\/\//i.test(url || '') ? url : `https://${url || ''}`).hostname.toLowerCase();
      return h === 'gitlab.com' || h === 'www.gitlab.com';
    } catch { return false; }
  };
  const sentryData = statusQuery.data?.sentry || {};
  const slackData = statusQuery.data?.slack || {};
  const larkData = statusQuery.data?.lark || {};
  // Defensively default to {} if the backend (Agent A's territory)
  // hasn't yet shipped these keys in the status payload — rendering as
  // "Connect" is the correct fallback for an absent provider.
  const openaiBillingData = statusQuery.data?.openai_billing || {};
  const anthropicBillingData = statusQuery.data?.anthropic_billing || {};
  const notionData = statusQuery.data?.notion || {};
  const planeData = statusQuery.data?.plane || {};
  const linearData = statusQuery.data?.linear || {};
  const circleciData = statusQuery.data?.circleci || {};
  const figmaData = statusQuery.data?.figma || {};

  return (
    <>
      <Helmet>
        <title>Integrations - Zibby</title>
        <meta name="description" content="Connect your favorite tools and services to Zibby." />
      </Helmet>
      <div className="page-layout main-body">
        <div className="page-header-center">
          <h1>Service Integrations</h1>
          <p>Connect your services to enable AI agents to create pull requests and link test executions</p>
        </div>

      <IonToast
        isOpen={toast.isOpen}
        onDidDismiss={() => setToast({ ...toast, isOpen: false })}
        message={toast.message}
        duration={5000}
        icon={toast.icon}
        position="bottom"
        cssClass="custom-toast"
      />

      <IonCard className="integrations-card">
        {/* Claude + OpenAI(Codex) DEPLOY credentials were REMOVED from this
            page — they aren't service integrations (PR/test-linking), they're
            per-PROJECT AI keys (project `secrets.aiOAuthToken` / `aiApiKey`).
            They're managed in Project Settings → AI and used by that
            project's apps + workflows; the deploy modal captures an ephemeral
            Claude token when a project has none yet. */}

        {/* GitHub Integration */}
        <IonItem
          className="integration-item"
        >
          <IonIcon icon={logoGithub} slot="start" className="integration-icon github" />
          <div className="integration-content">
            <h3>GitHub</h3>
            <p>{githubData.connected ? `Connected as ${githubData.username}` : 'Connect GitHub to enable AI agents to create pull requests'}</p>
            {githubData.connected && !githubData.isInstalled && !githubData.hasRepoScope && githubData.installUrl && (
              <p style={{ fontSize: '11px', color: 'var(--ion-color-warning)', marginTop: '4px', fontWeight: '500' }}>
                ⚠️ GitHub App not installed. <a href={githubData.installUrl} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: 'var(--ion-color-primary)', textDecoration: 'underline' }}>Install the app</a> to access private repositories.
              </p>
            )}
          </div>
          <IonButton
            className={`integration-button ${githubData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              githubData.connected ? showDisconnectConfirmation('github') : handleGithubConnect();
            }}
          >
            <IonIcon icon={githubData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {githubData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* GitLab — TWO PERMANENT, SEPARATE integration cards: one for a
            Self-Hosted instance, one for Cloud (GitLab.com). Both ALWAYS show
            (regardless of connection state) so you can connect each
            independently. Each card's connected/Connect state is derived from
            the integrations list: a self-hosted instance lights up the first
            card, a gitlab.com instance lights up the second. */}
        {(() => {
          const cloudInst = gitlabInstances.find((i) => isGitlabDotCom(i.instanceUrl));
          const selfInst = gitlabInstances.find((i) => !isGitlabDotCom(i.instanceUrl));
          const gitlabCard = (key, mode, title, inst, placeholder) => (
            <IonItem key={key} className="integration-item">
              {gitlabIcon}
              <div className="integration-content">
                <h3>{title}</h3>
                <p>{inst ? `Connected to ${inst.instanceUrl}${inst.username ? ` as ${inst.username}` : ''}` : placeholder}</p>
              </div>
              <IonButton
                className={`integration-button ${inst ? 'connected' : 'connect'}`}
                slot="end"
                onClick={(e) => {
                  e.stopPropagation();
                  inst ? showDisconnectConfirmation('gitlab', inst.integrationId) : handleGitlabConnect(mode);
                }}
              >
                <IonIcon icon={inst ? checkmarkCircle : linkOutline} slot="start" />
                {inst ? 'Connected' : 'Connect'}
              </IonButton>
            </IonItem>
          );
          return (
            <>
              {gitlabCard('gitlab-selfhosted', 'selfhosted', 'GitLab · Self-Hosted', selfInst, 'Connect a self-hosted GitLab instance to clone repositories and create merge requests')}
              {gitlabCard('gitlab-cloud', 'cloud', 'GitLab · Cloud', cloudInst, 'Connect GitLab.com to clone repositories and create merge requests')}
            </>
          );
        })()}

        {/* Jira Integration */}
        <IonItem
          className="integration-item"
        >
          <div className="jira-icon-wrapper" slot="start">
            <svg className="integration-icon jira" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.757a1.005 1.005 0 0 0-1.001-1zm5.7-5.757H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057a5.215 5.215 0 0 0 5.215 5.215V1.005A1.005 1.005 0 0 0 22.994 0z" fill="#2684FF"/>
            </svg>
          </div>
          <div className="integration-content">
            <h3>Jira</h3>
            <p>{jiraData.connected ? `Connected to ${jiraData.instanceUrl}` : 'Link test executions to Jira tickets and update status automatically'}</p>
          </div>
          <IonButton
            className={`integration-button ${jiraData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              jiraData.connected ? showDisconnectConfirmation('jira') : handleJiraConnect();
            }}
          >
            <IonIcon icon={jiraData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {jiraData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* Sentry Integration */}
        <IonItem className="integration-item">
          <div className="sentry-icon-wrapper" slot="start">
            <svg className="integration-icon sentry" viewBox="11 11 50 44" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
              {/* Sentry brand mark — coral/pink fill from their 2023+
                  identity. viewBox cropped to the path's natural bbox
                  (after its translate(11,11)) so the glyph fills the
                  26×26 wrapper edge-to-edge — matches the visual
                  weight of Jira / Lark / Slack siblings. */}
              <path d="M29,2.26a4.67,4.67,0,0,0-8,0L14.42,13.53A32.21,32.21,0,0,1,32.17,40.19H27.55A27.68,27.68,0,0,0,12.09,17.47L6,28a15.92,15.92,0,0,1,9.23,12.17H4.62A.76.76,0,0,1,4,39.06l2.94-5a10.74,10.74,0,0,0-3.36-1.9l-2.91,5a4.54,4.54,0,0,0,1.69,6.24A4.66,4.66,0,0,0,4.62,44H19.15a19.4,19.4,0,0,0-8-17.31l2.31-4A23.87,23.87,0,0,1,23.76,44H36.07a35.88,35.88,0,0,0-16.41-31.8l4.67-8a.77.77,0,0,1,1.05-.27c.53.29,20.29,34.77,20.66,35.17a.76.76,0,0,1-.68,1.13H40.6q.09,1.91,0,3.81h4.78A4.59,4.59,0,0,0,50,39.43a4.49,4.49,0,0,0-.62-2.28Z" transform="translate(11, 11)" fill="#E1557A"/>
            </svg>
          </div>
          <div className="integration-content">
            <h3>Sentry</h3>
            <p>{sentryData.connected ? `Connected to ${sentryData.organizationName || sentryData.organizationSlug}` : 'Connect Sentry to pull error and performance issues into your projects'}</p>
          </div>
          <IonButton
            className={`integration-button ${sentryData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              sentryData.connected ? showDisconnectConfirmation('sentry') : handleSentryConnect();
            }}
          >
            <IonIcon icon={sentryData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {sentryData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* Notion Integration — OAuth (not paste-token). The Connect
            button hits /notion/oauth/start on the llm-billing RestApi
            (Agent A's stack); backend redirects to api.notion.com,
            which calls back to /notion/callback, which finally
            redirects to /integrations?notion=success. */}
        <IonItem className="integration-item">
          <div className="notion-icon-wrapper" slot="start">
            <svg className="integration-icon notion" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }} aria-hidden="true">
              <path d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 1.968c-.42-.326-.981-.7-2.055-.607L3.01 2.295c-.466.046-.56.28-.374.466zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.887l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952L12.21 19s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.139c-.093-.514.28-.887.747-.933zM1.936 1.035l13.31-.98c1.634-.14 2.055-.047 3.082.7l4.249 2.986c.7.513.934.653.934 1.213v16.378c0 1.026-.373 1.634-1.68 1.726l-15.458.934c-.98.047-1.448-.093-1.962-.747l-3.129-4.06C.42 18.522.234 17.962.234 17.308V2.667c0-.839.374-1.54 1.703-1.632z" fill="#ffffff"/>
            </svg>
          </div>
          <div className="integration-content">
            <h3>Notion</h3>
            <p>{notionData.connected
              ? `Connected to ${notionData.workspaceName || 'Notion workspace'}`
              : 'Connect Notion to publish reports and notify pages from workflows'}</p>
          </div>
          <IonButton
            className={`integration-button ${notionData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              notionData.connected
                ? showDisconnectConfirmation('notion')
                : handleNotionConnect();
            }}
          >
            <IonIcon icon={notionData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {notionData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* Figma Integration — PASTE-TOKEN (personal access token), NOT OAuth.
            The Connect button opens FigmaConnectModal (1 field: PAT); the
            modal POSTs to /figma/connect on the llm-billing RestApi. Backend
            validates the PAT (Figma /v1/me with X-Figma-Token) + stores it;
            the figmaSkill resolves it per-call and authenticates with the
            X-Figma-Token header. (Dormant OAuth routes remain for legacy rows.) */}
        <IonItem className="integration-item">
          <div className="figma-icon-wrapper" slot="start">
            {/* Figma logomark — the canonical five-circle/half-circle
                multi-color brand mark. */}
            <svg className="integration-icon figma" viewBox="0 0 38 57" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }} aria-hidden="true">
              <path d="M19 28.5a9.5 9.5 0 1 1 19 0 9.5 9.5 0 0 1-19 0z" fill="#1ABCFE"/>
              <path d="M0 47.5A9.5 9.5 0 0 1 9.5 38H19v9.5a9.5 9.5 0 1 1-19 0z" fill="#0ACF83"/>
              <path d="M19 0v19h9.5a9.5 9.5 0 1 0 0-19H19z" fill="#FF7262"/>
              <path d="M0 9.5A9.5 9.5 0 0 0 9.5 19H19V0H9.5A9.5 9.5 0 0 0 0 9.5z" fill="#F24E1E"/>
              <path d="M0 28.5A9.5 9.5 0 0 0 9.5 38H19V19H9.5A9.5 9.5 0 0 0 0 28.5z" fill="#A259FF"/>
            </svg>
          </div>
          <div className="integration-content">
            <h3>Figma</h3>
            <p>{figmaData.connected
              ? `Connected${figmaData.handle ? ` as ${figmaData.handle}` : ''}`
              : 'Connect Figma to read files, frames, and comments and render designs from workflows'}</p>
          </div>
          <IonButton
            className={`integration-button ${figmaData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              figmaData.connected
                ? showDisconnectConfirmation('figma')
                : handleFigmaConnect();
            }}
          >
            <IonIcon icon={figmaData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {figmaData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* CircleCI Integration — api-key (paste-token), NOT OAuth (CircleCI
            has no consumer OAuth). The Connect button opens CircleCIConnectModal
            (1 field: API token); the modal POSTs to /circleci/connect on the
            llm-billing RestApi. Backend validates (GET /api/v2/me with the
            Circle-Token header) + stores; the flaky-test-fixer's deterministic
            collect_flaky node resolves it via resolveIntegrationToken('circleci')
            to call the CircleCI Insights API directly (no MCP server). */}
        <IonItem className="integration-item">
          <div className="circleci-icon-wrapper" slot="start">
            {/* Official CircleCI icon (vectorlogo.zone) — dark mark on a white
                rounded square for contrast against the dark integrations list. */}
            <svg className="integration-icon circleci" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }} aria-hidden="true">
              <rect width="64" height="64" rx="14" fill="#fff" />
              <g transform="matrix(2.791226 0 0 2.791226 71.368826 91.588681)" fill="#161616">
                <circle r="2.73" cy="-21.349" cx="-14.275" />
                <path d="M-14.275-32.813c-5.342 0-9.83 3.654-11.103 8.598a.56.56 0 0 0-.019.136c0 .3.244.546.546.546h4.623a.54.54 0 0 0 .495-.32s.007-.013.01-.02c.953-2.056 3.034-3.483 5.45-3.483 3.317 0 6.006 2.688 6.006 6.005s-2.688 6.005-6.005 6.005c-2.415 0-4.496-1.427-5.45-3.483-.003-.007-.01-.02-.01-.02-.088-.194-.282-.32-.495-.32h-4.623c-.302 0-.546.244-.546.546a.54.54 0 0 0 .019.136c1.273 4.944 5.762 8.598 11.103 8.598C-7.942-9.89-2.8-15.022-2.8-21.353s-5.133-11.464-11.464-11.464z" />
              </g>
            </svg>
          </div>
          <div className="integration-content">
            <h3>CircleCI</h3>
            <p>{circleciData.connected
              ? `Connected${circleciData.userLogin ? ` · ${circleciData.userLogin}` : ''} · token ${circleciData.keyMask || '••••'}`
              : 'Paste a personal API token so the flaky-test-fixer agent can read flaky tests from CircleCI Insights'}</p>
          </div>
          <IonButton
            className={`integration-button ${circleciData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              circleciData.connected
                ? showDisconnectConfirmation('circleci')
                : handleCircleciConnect();
            }}
          >
            <IonIcon icon={circleciData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {circleciData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* Lark Integration */}
        <IonItem className="integration-item">
          <div className="lark-icon-wrapper" slot="start">
            <svg className="integration-icon lark" viewBox="62.16 94.5 407.87 324.19" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
              <path d="M274.18 264.785q.515-.517 1.03-1.027c.685-.688 1.372-1.258 2.056-1.945l1.37-1.372 4.118-4.113 5.598-5.601 4.8-4.797 4.575-4.457 4.796-4.688 4.344-4.344 6.059-6.054c1.14-1.145 2.285-2.29 3.543-3.317 2.168-2.054 4.457-4 6.855-5.828 2.172-1.715 4.344-3.312 6.516-4.914 3.082-2.172 6.398-4.344 9.71-6.285 3.204-1.941 6.63-3.656 10.06-5.371 3.199-1.602 6.515-2.973 9.827-4.23 1.829-.684 3.774-1.372 5.602-2.055.914-.344 1.941-.688 2.856-.914-8.57-33.715-24.227-64.575-45.258-90.86-4.114-5.14-10.399-8.113-17.028-8.113H130.754c-3.203 0-4.457 4-1.945 5.941 59.543 43.66 109.144 99.887 145.03 164.801 0-.226.227-.34.34-.457m0 0" fill="#00d6b9"/>
              <path d="M204.79 418.691c90.288 0 169.03-49.828 210.058-123.543 1.488-2.628 2.859-5.257 4.23-7.882q-3.087 6-6.86 11.312l-2.741 3.77c-1.141 1.488-2.399 2.972-3.657 4.457-1.03 1.144-2.058 2.285-3.086 3.316-2.058 2.172-4.343 4.227-6.629 6.172a53 53 0 0 1-3.886 3.2c-1.598 1.144-3.086 2.284-4.684 3.429-1.031.683-2.058 1.371-3.086 1.941-1.144.684-2.172 1.258-3.316 1.942a131 131 0 0 1-6.969 3.543c-2.059.918-4.117 1.828-6.289 2.515-2.285.801-4.57 1.602-6.969 2.285-3.543.914-7.086 1.715-10.742 2.286-2.629.457-5.258.687-8 .914-2.86.23-5.601.23-8.457.23-3.086 0-6.289-.23-9.488-.57a83 83 0 0 1-7.086-1.031c-2.055-.34-4.113-.801-6.168-1.258-1.031-.227-2.176-.57-3.203-.797-2.973-.8-6.055-1.602-9.028-2.516-1.488-.457-2.972-.914-4.457-1.258-2.172-.683-4.457-1.37-6.629-2.058-1.828-.57-3.656-1.14-5.37-1.711q-2.573-.86-5.145-1.715c-1.14-.344-2.285-.8-3.543-1.144-1.371-.457-2.856-1.028-4.227-1.485-1.027-.344-2.058-.687-2.972-1.027-1.942-.688-4-1.488-5.942-2.172-1.144-.457-2.285-.914-3.43-1.258-1.484-.57-3.085-1.144-4.57-1.828-1.601-.687-3.203-1.258-4.8-1.945-1.028-.457-2.06-.797-3.087-1.258-1.257-.57-2.628-1.027-3.886-1.598-1.028-.457-1.942-.8-2.969-1.258l-3.086-1.37c-.914-.344-1.832-.801-2.746-1.145a44 44 0 0 1-2.512-1.14c-.8-.345-1.715-.802-2.515-1.145-.914-.344-1.715-.801-2.512-1.141-1.031-.457-2.172-1.031-3.203-1.484-1.14-.575-2.285-1.032-3.426-1.602-1.258-.574-2.402-1.144-3.66-1.715-1.027-.457-2.055-1.027-3.082-1.484-54.172-26.973-102.172-63.086-143.09-106.746-2.055-2.172-5.71-.684-5.71 2.289l.112 154.398v12.57c0 7.317 3.543 14.06 9.598 18.172 38.172 24.801 83.773 39.543 132.914 39.543m0 0" fill="#3370ff"/>
              <path d="M470.035 201.121c-18.285-9.031-38.86-14.059-60.687-14.059-12.914 0-25.485 1.829-37.371 5.141-1.372.344-2.743.8-4.114 1.258-.914.344-1.941.574-2.855.914-1.945.688-3.774 1.375-5.602 2.059-3.316 1.257-6.629 2.742-9.828 4.23-3.43 1.598-6.742 3.426-10.058 5.371a128 128 0 0 0-9.715 6.285c-2.285 1.602-4.457 3.2-6.512 4.914a154 154 0 0 0-6.86 5.828c-1.14 1.141-2.398 2.172-3.542 3.313l-6.055 6.059-4.344 4.343-4.8 4.684-4.57 4.46-4.802 4.798-11.086 11.086c-.687.687-1.37 1.37-2.058 1.945l-1.028 1.027c-.457.457-1.027 1.028-1.601 1.485-.57.57-1.14 1.031-1.711 1.601a244.4 244.4 0 0 1-49.828 35.313c1.027.457 2.168 1.027 3.199 1.488.8.34 1.715.797 2.512 1.14.8.344 1.715.801 2.515 1.145.801.344 1.602.684 2.516 1.14.914.345 1.828.802 2.742 1.145l3.086 1.371c1.027.457 1.942.801 2.969 1.258 1.258.57 2.629 1.028 3.887 1.598 1.03.46 2.058.8 3.086 1.258 1.601.687 3.199 1.258 4.8 1.945 1.485.57 3.086 1.14 4.57 1.828 1.145.457 2.286.914 3.43 1.258 1.946.684 4 1.484 5.946 2.172a81 81 0 0 1 2.968 1.027c1.371.457 2.856 1.028 4.23 1.485 1.141.343 2.286.8 3.544 1.14q2.567.86 5.14 1.719c1.829.57 3.657 1.14 5.372 1.71 2.171.688 4.457 1.376 6.628 2.06 1.489.457 2.973.914 4.457 1.257 2.973.914 5.942 1.715 9.032 2.512 1.027.344 2.168.574 3.199.8 2.055.458 4.113.915 6.172 1.259 2.398.457 4.683.8 7.082 1.03 3.203.34 6.402.571 9.488.571 2.856 0 5.715 0 8.457-.23 2.63-.227 5.371-.457 8-.914 3.656-.57 7.2-1.371 10.742-2.286 2.399-.683 4.688-1.37 6.973-2.285 2.172-.8 4.227-1.601 6.285-2.515 2.399-1.028 4.684-2.285 6.973-3.543 1.14-.57 2.168-1.258 3.312-1.942 1.028-.687 2.059-1.257 3.086-1.945 1.602-1.027 3.2-2.168 4.684-3.426a52 52 0 0 0 3.887-3.203c2.289-1.941 4.457-4 6.628-6.168 1.032-1.031 2.06-2.172 3.086-3.316 1.258-1.485 2.516-2.969 3.657-4.457.918-1.258 1.828-2.512 2.742-3.77 2.515-3.543 4.8-7.316 6.86-11.199l2.284-4.688 21.145-42.171v.113c6.742-14.742 16.226-28.113 27.656-39.426m0 0" fill="#133c9a"/>
            </svg>
          </div>
          <div className="integration-content">
            <h3>Lark</h3>
            <p>{larkData.connected ? `Connected as app ${larkData.appId}${larkData.host === 'https://open.feishu.cn' ? ' · Feishu (CN)' : ' · Lark (Intl)'}${larkData.webhookReady ? ' · webhook ready' : ' · outbound only'}` : 'Connect a Lark self-built app to send messages and receive bot events'}</p>
            {larkData.connected && larkData.eventRequestUrl && (
              <p style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                Event Request URL (paste into Lark → Event Subscriptions):{' '}
                <code
                  style={{ cursor: 'pointer', wordBreak: 'break-all' }}
                  title="Click to copy"
                  onClick={(e) => {
                    e.stopPropagation();
                    try {
                      navigator.clipboard.writeText(larkData.eventRequestUrl);
                      showToast('Event Request URL copied', 'success');
                    } catch { /* clipboard unavailable */ }
                  }}
                >{larkData.eventRequestUrl}</code>
              </p>
            )}
          </div>
          <IonButton
            className={`integration-button ${larkData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              larkData.connected ? showDisconnectConfirmation('lark') : handleLarkConnect();
            }}
          >
            <IonIcon icon={larkData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {larkData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* Slack Integration */}
        <IonItem className="integration-item">
          <div className="slack-icon-wrapper" slot="start">
            <svg className="integration-icon slack" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }}>
              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A"/>
              <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0"/>
              <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.27 0a2.528 2.528 0 0 1-2.522 2.521 2.527 2.527 0 0 1-2.521-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.522 2.522v6.312z" fill="#2EB67D"/>
              <path d="M15.165 18.956a2.528 2.528 0 0 1 2.522 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.521-2.522v-2.522h2.521zm0-1.27a2.527 2.527 0 0 1-2.521-2.522 2.528 2.528 0 0 1 2.521-2.522h6.313A2.528 2.528 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.521h-6.313z" fill="#ECB22E"/>
            </svg>
          </div>
          <div className="integration-content">
            <h3>Slack</h3>
            <p>{slackData.connected ? `Connected to ${slackData.teamName}` : 'Send notifications and messages to Slack channels and DMs'}</p>
          </div>
          <IonButton
            className={`integration-button ${slackData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              slackData.connected ? showDisconnectConfirmation('slack') : handleSlackConnect();
            }}
          >
            <IonIcon icon={slackData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {slackData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* OpenAI Admin Integration — paste-token, org-admin scope.
            See backend handlers/llm-billing.js for the validation
            probe. Connected state shows the masked key (last 4 chars
            via backend's metadata.keyMask). */}
        <IonItem className="integration-item">
          <div className="openai-icon-wrapper" slot="start">
            <svg className="integration-icon openai" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }} aria-hidden="true">
              <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" fill="#ffffff"/>
            </svg>
          </div>
          <div className="integration-content">
            <h3>OpenAI Admin</h3>
            <p>{openaiBillingData.connected
              ? `Connected · key ${openaiBillingData.keyMask || '••••'}`
              : 'Paste an org admin key to track daily OpenAI spend'}</p>
          </div>
          <IonButton
            className={`integration-button ${openaiBillingData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              openaiBillingData.connected
                ? showDisconnectConfirmation('openai_billing')
                : handleOpenAIBillingConnect();
            }}
          >
            <IonIcon icon={openaiBillingData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {openaiBillingData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* Anthropic Admin Integration */}
        <IonItem className="integration-item">
          <div className="anthropic-icon-wrapper" slot="start">
            <svg className="integration-icon anthropic" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }} aria-hidden="true">
              <path d="M13.827 3.52h3.603L24 20h-3.603l-6.57-16.48zm-7.258 0h3.767L16.906 20h-3.674l-1.343-3.461H4.997L3.622 20H0L6.57 3.52zm4.107 9.967L8.453 7.687 6.205 13.487H10.676z" fill="#D97757"/>
            </svg>
          </div>
          <div className="integration-content">
            <h3>Anthropic Admin</h3>
            <p>{anthropicBillingData.connected
              ? `Connected · key ${anthropicBillingData.keyMask || '••••'}`
              : 'Paste an org admin key to track daily Anthropic spend'}</p>
          </div>
          <IonButton
            className={`integration-button ${anthropicBillingData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              anthropicBillingData.connected
                ? showDisconnectConfirmation('anthropic_billing')
                : handleAnthropicBillingConnect();
            }}
          >
            <IonIcon icon={anthropicBillingData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {anthropicBillingData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* Plane Integration — api-key (paste-token), NOT OAuth. The
            Connect button opens PlaneConnectModal (3 fields: API key,
            workspace slug, base URL); the modal POSTs to /plane/connect
            on the llm-billing RestApi. Backend validates + stores; the
            workflow-executor injects PLANE_API_KEY / PLANE_WORKSPACE_SLUG
            / PLANE_BASE_URL so the planeSkill can spawn the official
            plane-mcp-server. */}
        <IonItem className="integration-item">
          <div className="plane-icon-wrapper" slot="start">
            <svg className="integration-icon plane" viewBox="0 0 680 680" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }} aria-hidden="true">
              <rect width="680" height="680" rx="150" fill="#fff" />
              <g transform="translate(50.5,159.5)">
                <path d="M0 20.6413C0 5.3174 16.1 -4.64793 29.7966 2.20067L147.972 61.3502V200.168C147.972 219.674 158.992 237.527 176.417 246.248L283.068 299.63V340.352C283.068 355.675 266.968 365.641 253.271 358.779L28.4447 246.248C11.0201 237.527 0 219.688 0 200.168V20.6413ZM147.972 20.6413C147.972 5.3174 164.072 -4.64793 177.769 2.21435L295.945 61.3638V200.182C295.945 219.688 306.965 237.541 324.39 246.262L431.041 299.643V340.366C431.041 355.689 414.94 365.654 401.244 358.792L283.068 299.643V160.826C283.068 141.306 272.048 123.466 254.623 114.744L147.972 61.3638V20.6413ZM295.931 20.6413C295.931 5.3174 312.032 -4.64793 325.728 2.21435L550.555 114.744C567.993 123.466 579 141.306 579 160.826V340.366C579 355.689 562.899 365.654 549.203 358.792L431.027 299.643V160.826C431.027 141.306 420.007 123.466 402.582 114.744L295.931 61.3638V20.6413Z" fill="#121212" />
              </g>
            </svg>
          </div>
          <div className="integration-content">
            <h3>Plane</h3>
            <p>{planeData.connected
              ? `Connected · ${planeData.workspaceSlug || 'workspace'} · key ${planeData.keyMask || '••••'}`
              : 'Paste an API key + workspace slug to manage projects & work items from workflows'}</p>
          </div>
          <IonButton
            className={`integration-button ${planeData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              planeData.connected
                ? showDisconnectConfirmation('plane')
                : handlePlaneConnect();
            }}
          >
            <IonIcon icon={planeData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {planeData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* Linear Integration — api-key (paste-token), NOT OAuth. The
            Connect button opens LinearConnectModal (1 field: API key);
            the modal POSTs to /linear/connect on the llm-billing RestApi.
            Backend validates (Linear viewer query) + stores; the
            workflow-executor injects LINEAR_API_KEY so the linearSkill can
            call the Linear GraphQL API directly (no MCP server). */}
        <IonItem className="integration-item">
          <div className="linear-icon-wrapper" slot="start">
            {/* Linear logomark: indigo (#5E6AD2) rounded square with the
                geometric three-arc / diagonal mark. Faithful to Linear's
                brand. */}
            <svg className="integration-icon linear" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }} aria-hidden="true">
              <rect width="100" height="100" rx="22" fill="#5E6AD2" />
              <g fill="#fff">
                <path d="M21.62 60.49a31.5 31.5 0 0 0 17.89 17.89L21.62 60.49Z" />
                <path d="M20.3 50.36 49.64 79.7a31.7 31.7 0 0 1-5.38-.7L21 55.74a31.7 31.7 0 0 1-.7-5.38Z" />
                <path d="M20.86 41.62 58.38 79.14a31.6 31.6 0 0 1-4.34 1.18L19.68 45.96a31.6 31.6 0 0 1 1.18-4.34Z" />
                <path d="M23.34 35.62 64.38 76.66a31.9 31.9 0 0 1-3.56 1.96l-39.7-39.7a31.9 31.9 0 0 1 1.96-3.56l.26-.74Z" />
                <path d="M28.43 28.43c11.95-11.95 31.33-11.95 43.28 0s11.95 31.33 0 43.28L28.43 28.43Z" />
              </g>
            </svg>
          </div>
          <div className="integration-content">
            <h3>Linear</h3>
            <p>{linearData.connected
              ? `Connected${linearData.userName ? ` · ${linearData.userName}` : ''} · key ${linearData.keyMask || '••••'}`
              : 'Paste a personal API key to read & update Linear issues from workflows'}</p>
          </div>
          <IonButton
            className={`integration-button ${linearData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              linearData.connected
                ? showDisconnectConfirmation('linear')
                : handleLinearConnect();
            }}
          >
            <IonIcon icon={linearData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {linearData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

        {/* OpenDesign Integration — MANUAL connect (Instance URL + API token),
            NOT OAuth. The Connect button opens OpenDesignConnectModal (2 fields:
            Instance URL + OD_API_TOKEN bearer); the modal POSTs to
            /integrations/open-design on the main api-prod RestApi. On open the
            modal also calls /integrations/open-design/suggest to auto-prefill the
            project's own deployed instance. Status + disconnect use the same
            /integrations/open-design route, so OpenDesign carries its own local
            state (openDesignData) rather than the aggregated /integrations/status
            payload. */}
        <IonItem className="integration-item">
          <div className="open-design-icon-wrapper" slot="start">
            {/* Placeholder mark — no official OpenDesign logo asset in the repo
                yet. A neutral rounded-square "OD" monogram consistent with the
                sibling icons' 26×26 footprint. Swap for the real brand SVG /
                public/icons asset when available. */}
            <svg className="integration-icon open-design" viewBox="0 0 82 83" xmlns="http://www.w3.org/2000/svg" style={{ width: 26, height: 26 }} aria-hidden="true">
              <path d="M82 41.7266C82 77.3019 76.5753 82.7266 41 82.7266C5.42465 82.7266 0 77.3019 0 41.7266C0 6.15121 5.42465 0.726562 41 0.726562C76.5753 0.726562 82 6.15121 82 41.7266Z" fill="#202020"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M40.8904 16.4258C54.8646 16.4258 66.193 27.7541 66.193 41.7283C66.193 55.7025 54.8646 67.0309 40.8904 67.0309H18.9543C17.0911 67.0309 15.588 65.528 15.588 63.6648C15.588 58.0216 15.5879 46.7754 15.5879 41.7283C15.5879 27.7541 26.9162 16.4258 40.8904 16.4258ZM40.8905 21.4864C29.7112 21.4864 20.6485 30.5491 20.6485 41.7284C20.6485 52.9078 29.7112 61.9704 40.8905 61.9704C52.0699 61.9704 61.1325 52.9078 61.1325 41.7284C61.1325 30.5491 52.0699 21.4864 40.8905 21.4864Z" fill="white"/>
              <path d="M39.3144 53.6047L31.6001 33.3039C31.3504 32.6469 31.9921 32.0028 32.6447 32.2553L52.955 40.113C53.7909 40.4364 53.5606 41.6841 52.665 41.6841H40.8769V53.3146C40.8769 54.2163 39.6345 54.447 39.3144 53.6047Z" fill="white"/>
            </svg>
          </div>
          <div className="integration-content">
            <h3>OpenDesign</h3>
            <p>{openDesignData.connected
              ? `Connected to ${openDesignData.baseUrl || 'instance'}`
              : 'Connect a deployed OpenDesign instance so agents can read designs, export PDFs, and drive its Local CLI.'}</p>
          </div>
          <IonButton
            className={`integration-button ${openDesignData.connected ? 'connected' : 'connect'}`}
            slot="end"
            onClick={(e) => {
              e.stopPropagation();
              openDesignData.connected
                ? handleOpenDesignDisconnect()
                : handleOpenDesignConnect();
            }}
          >
            <IonIcon icon={openDesignData.connected ? checkmarkCircle : linkOutline} slot="start" />
            {openDesignData.connected ? 'Connected' : 'Connect'}
          </IonButton>
        </IonItem>

      </IonCard>

      {/* AI Agent MCP — sits BELOW the per-service integrations so the
          flow reads "wire up your accounts → then connect your agent". */}
      <McpInstallCard />

      <GitlabConnectModal
        isOpen={gitlabModalOpen}
        mode={gitlabMode}
        onClose={() => setGitlabModalOpen(false)}
        onConnected={() => showToast('GitLab connected successfully!', 'success')}
      />

      <LarkConnectModal
        isOpen={larkModalOpen}
        onClose={() => setLarkModalOpen(false)}
        onConnected={({ webhookReady } = {}) => {
          showToast(webhookReady ? 'Lark connected — webhook ready.' : 'Lark connected successfully!', 'success');
        }}
      />

      <OpenAIBillingConnectModal
        isOpen={openaiBillingModalOpen}
        onClose={() => setOpenaiBillingModalOpen(false)}
        onConnected={() => showToast('OpenAI Admin connected successfully!', 'success')}
      />

      <AnthropicBillingConnectModal
        isOpen={anthropicBillingModalOpen}
        onClose={() => setAnthropicBillingModalOpen(false)}
        onConnected={() => showToast('Anthropic Admin connected successfully!', 'success')}
      />

      <PlaneConnectModal
        isOpen={planeModalOpen}
        onClose={() => setPlaneModalOpen(false)}
        onConnected={() => showToast('Plane connected successfully!', 'success')}
      />

      <LinearConnectModal
        isOpen={linearModalOpen}
        onClose={() => setLinearModalOpen(false)}
        onConnected={() => showToast('Linear connected successfully!', 'success')}
      />

      <CircleCIConnectModal
        isOpen={circleciModalOpen}
        onClose={() => setCircleciModalOpen(false)}
        onConnected={() => showToast('CircleCI connected successfully!', 'success')}
      />

      <FigmaConnectModal
        isOpen={figmaModalOpen}
        onClose={() => setFigmaModalOpen(false)}
        onConnected={() => showToast('Figma connected successfully!', 'success')}
      />

      <OpenDesignConnectModal
        isOpen={openDesignModalOpen}
        onClose={() => setOpenDesignModalOpen(false)}
        onConnected={() => {
          refreshOpenDesign();
          showToast('OpenDesign connected successfully!', 'success');
        }}
      />

      {/* Disconnect Confirmation Alert */}
      <IonAlert
        isOpen={disconnectAlert.isOpen}
        onDidDismiss={handleDisconnectCancelled}
        header={`Disconnect ${{ jira: 'Jira', github: 'GitHub', gitlab: 'GitLab', sentry: 'Sentry', slack: 'Slack', lark: 'Lark', openai_billing: 'OpenAI Admin', anthropic_billing: 'Anthropic Admin', notion: 'Notion', plane: 'Plane', linear: 'Linear', circleci: 'CircleCI', figma: 'Figma' }[disconnectAlert.type] || 'Service'}`}
        message={`Are you sure you want to disconnect ${{ jira: 'Jira', github: 'GitHub', gitlab: 'GitLab', sentry: 'Sentry', slack: 'Slack', lark: 'Lark', openai_billing: 'OpenAI Admin', anthropic_billing: 'Anthropic Admin', notion: 'Notion', plane: 'Plane', linear: 'Linear', circleci: 'CircleCI', figma: 'Figma' }[disconnectAlert.type] || 'this service'}?`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            handler: handleDisconnectCancelled,
          },
          {
            text: 'Disconnect',
            handler: handleDisconnectConfirmed,
          },
        ]}
      />
      </div>
    </>
  );
};

export default IntegrationsPage;
